import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  BookOpen,
  ClipboardCheck,
  Compass,
  FileText,
  Lock,
  MessageCircle,
  Menu,
  ShieldCheck,
  Sparkles,
  Users,
  Award,
  RefreshCw,
  Printer,
  AlertTriangle,
  CheckCircle2,
} from "lucide-react";
import { PortalHeader, useLearner } from "./PortalDashboard";
import { previewStore } from "./previewStore";
import PreviewBanner from "./PreviewBanner";
import api from "../../lib/api";
import fd2gApi from "./fd2gApi";
import usePageMeta from "../../lib/usePageMeta";

const COURSE_CODE = "FD2G-FAC-001";

const SECTION_ICON = {
  "start-here": Compass,
  "five-voices": Sparkles,
  "facilitator-guide": BookOpen,
  "choosing-format": Users,
  "opening": MessageCircle,
  "question-bank": ClipboardCheck,
  "voice-map": FileText,
  "difficult-moments": AlertTriangle,
  "boundaries-referral": ShieldCheck,
  "participant-materials": FileText,
  "branding-credential-use": Award,
  "program-updates": RefreshCw,
};

export default function PortalFd2gFacResourceCenter() {
  usePageMeta({
    title: "FD2G Certified Facilitator Resource Center | CCCHAPS Academy Preview",
    noindex: true,
  });

  const inPreview = previewStore.isActive() && previewStore.getCourseCode() === COURSE_CODE;
  const learner = useLearner();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState("start-here");
  const [navOpen, setNavOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const fd2g = fd2gApi();
        const r = await fd2g.get("resource-center");
        setData(r.data);
      } catch (err) {
        const msg = err?.response?.data?.detail || "Resource Center is unavailable.";
        toast.error(typeof msg === "string" ? msg : "Resource Center is unavailable.");
        nav("/academy/portal/fd2g-fac-001/learn");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFCF6]">
        <PortalHeader learner={learner} />
        <div className="p-12 text-center text-slate-500">Loading Resource Center…</div>
      </div>
    );
  }

  if (!data?.access) {
    return <ResourceCenterDenied data={data} />;
  }

  const sections = data.sections || [];
  const activeSection = sections.find((s) => s.id === activeId) || sections[0];

  return (
    <div className="bg-[#FDFCF6] min-h-screen" data-testid="portal-fd2gfac-rc">
      <PreviewBanner course={{ code: COURSE_CODE, title: "Certified Facilitator Resource Center" }}
                     resetTo="/academy/portal/fd2g-fac-001/resource-center" />
      <PortalHeader learner={learner} />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 md:py-10">
        <HeaderCard header={data.header} credential={data.credential} expiringNotice={data.expiring_notice} />

        <div className="mt-6 grid lg:grid-cols-[280px_1fr] gap-6">
          {/* Sidebar */}
          <aside className={`${navOpen ? "block" : "hidden"} lg:block`} data-testid="fd2gfac-rc-sidebar">
            <div className="bg-white border border-slate-200 rounded-2xl p-3 sticky top-24">
              <p className="text-[10px] uppercase tracking-[0.22em] text-[#4A6FA5] font-semibold px-2 py-1">Sections</p>
              <ul className="mt-1 space-y-0.5 text-sm">
                {sections.map((s) => {
                  const Icon = SECTION_ICON[s.id] || FileText;
                  const isActive = s.id === activeSection?.id;
                  return (
                    <li key={s.id}>
                      <button
                        onClick={() => { setActiveId(s.id); setNavOpen(false); window.scrollTo({ top: 260, behavior: "smooth" }); }}
                        data-testid={`fd2gfac-rc-nav-${s.id}`}
                        className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-md text-left ${isActive ? "bg-[#F0F4F8] text-[#3B597B] font-medium" : "text-slate-600 hover:bg-slate-50"}`}
                      >
                        <Icon className="w-3.5 h-3.5 text-[#4A6FA5]" />
                        <span className="truncate text-[13px]">{s.order}. {s.title}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          </aside>

          <main className="min-w-0">
            <div className="lg:hidden mb-3 flex justify-end">
              <button onClick={() => setNavOpen((v) => !v)}
                      className="inline-flex items-center gap-2 text-sm text-slate-600"
                      data-testid="fd2gfac-rc-nav-toggle">
                <Menu className="w-4 h-4" /> {navOpen ? "Hide sections" : "Show sections"}
              </button>
            </div>
            {activeSection && <SectionRenderer section={activeSection} />}
            <div className="mt-8">
              <Link to="/academy/portal/fd2g-fac-001/learn" data-testid="fd2gfac-rc-back-btn"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-300 text-sm text-slate-700 hover:border-[#4A6FA5]">
                <ArrowLeft className="w-4 h-4" /> Back to Course
              </Link>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}

function HeaderCard({ header, credential, expiringNotice }) {
  const fmt = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }); }
    catch (_) { return iso; }
  };
  const status = credential?.status || "ACTIVE";
  const badge = status === "ACTIVE"
    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : status === "EXPIRING SOON"
    ? "bg-amber-50 text-amber-800 border-amber-200"
    : "bg-slate-50 text-slate-700 border-slate-200";
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8" data-testid="fd2gfac-rc-header">
      <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#4A6FA5]">{header?.eyebrow}</p>
      <h1 className="mt-2 font-heading text-2xl md:text-3xl text-slate-900 leading-tight">{header?.title}</h1>
      {header?.supporting_text && (
        <p className="mt-2 text-slate-600">{header.supporting_text}</p>
      )}
      {credential && (
        <div className="mt-5 rounded-lg bg-[#FBF8F1] border-l-4 border-[#C9A55C] px-4 py-3" data-testid="fd2gfac-rc-credential-summary">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-slate-900 font-semibold">{credential.credential_name}</p>
              <p className="mt-1 text-[12px] text-slate-600">
                Credential ID: <span className="font-mono">{credential.credential_id}</span>
                &nbsp;·&nbsp; Valid Through: <strong>{fmt(credential.valid_through)}</strong>
              </p>
            </div>
            <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] border ${badge}`}
                  data-testid="fd2gfac-rc-status-badge">
              <span className="w-1.5 h-1.5 rounded-full bg-current" />{status}
            </span>
          </div>
          {expiringNotice && (
            <p className="mt-3 text-[12px] text-amber-800" data-testid="fd2gfac-rc-expiring-notice">{expiringNotice}</p>
          )}
        </div>
      )}
      {header?.primary_principle && (
        <blockquote className="mt-5 rounded-lg bg-[#F0F4F8] border-l-4 border-[#4A6FA5] px-4 py-3 text-slate-800 italic">
          "{header.primary_principle}"
        </blockquote>
      )}
    </div>
  );
}

function ResourceCenterDenied({ data }) {
  const d = data?.denial || { heading: "RESOURCE CENTER ACCESS UNAVAILABLE", body: "", note: "" };
  return (
    <div className="bg-[#FDFCF6] min-h-screen">
      <PortalHeader />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-16" data-testid="fd2gfac-rc-denied">
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-6 py-8 text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-rose-100 text-rose-700 mb-4">
            <Lock className="w-5 h-5" />
          </div>
          <h1 className="font-heading text-xl text-rose-900" data-testid="fd2gfac-rc-denied-heading">{d.heading}</h1>
          {d.body && <p className="mt-3 text-sm text-rose-900">{d.body}</p>}
          {d.note && <p className="mt-2 text-[12px] italic text-rose-800">{d.note}</p>}
        </div>
        <div className="mt-6 text-center">
          <Link to="/academy/portal/fd2g-fac-001/learn"
                className="inline-flex items-center gap-2 text-sm font-medium text-[#4A6FA5] hover:underline">
            <ArrowLeft className="w-4 h-4" /> Back to Course
          </Link>
        </div>
      </div>
    </div>
  );
}

function SectionRenderer({ section }) {
  return (
    <article className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8" data-testid={`fd2gfac-rc-section-${section.id}`}>
      {section.eyebrow && (
        <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#4A6FA5]">{section.eyebrow}</p>
      )}
      <h1 className="mt-2 font-heading text-xl md:text-2xl text-slate-900 leading-tight">{section.title}</h1>
      {section.subtitle && <p className="mt-2 text-slate-600 text-sm">{section.subtitle}</p>}
      <div className="mt-5">
        {section.kind === "checklist" && <ChecklistBody section={section} />}
        {section.kind === "voice_reference" && <VoiceReferenceBody section={section} />}
        {section.kind === "official_file" && <OfficialFileBody section={section} />}
        {section.kind === "format_cards" && <FormatCardsBody section={section} />}
        {section.kind === "opening_framework" && <OpeningBody section={section} />}
        {section.kind === "question_bank" && <QuestionBankBody section={section} />}
        {section.kind === "voice_map" && <VoiceMapBody section={section} />}
        {section.kind === "scenarios" && <ScenariosBody section={section} />}
        {section.kind === "boundaries" && <BoundariesBody section={section} />}
        {section.kind === "material_cards" && <MaterialsBody section={section} />}
        {section.kind === "branding" && <BrandingBody section={section} />}
        {section.kind === "updates" && <UpdatesBody section={section} />}
      </div>
    </article>
  );
}

function ChecklistBody({ section }) {
  return (
    <div className="space-y-4">
      {(section.sections || []).map((sec, i) => (
        <div key={i}>
          <p className="text-[11px] uppercase tracking-[0.16em] font-semibold text-slate-500">{sec.heading}</p>
          <ul className="mt-2 space-y-2 text-sm text-slate-800">
            {sec.items.map((it, j) => (
              <li key={j} className="flex items-start gap-2">
                <span className="mt-0.5 inline-flex w-4 h-4 border border-slate-400 rounded-sm shrink-0" />
                <span>{it}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
      {section.reminder && (
        <div className="mt-4 rounded-lg bg-[#F0F4F8] border-l-4 border-[#4A6FA5] px-4 py-3">
          <p className="text-[10px] uppercase tracking-[0.16em] font-semibold text-[#3B597B]">{section.reminder.label}</p>
          <p className="mt-1 text-slate-900 italic">"{section.reminder.text}"</p>
        </div>
      )}
    </div>
  );
}

function VoiceReferenceBody({ section }) {
  return (
    <div className="space-y-5">
      {(section.voices || []).map((v) => (
        <div key={v.key} className="rounded-lg border border-slate-200 bg-white p-4">
          <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#4A6FA5]">{v.key} <span className="text-slate-500">· {v.subtitle}</span></p>
          <p className="mt-2 text-[13px] uppercase tracking-[0.12em] font-semibold text-slate-500">Question</p>
          <p className="mt-1 text-slate-800 italic">"{v.question}"</p>
          {v.practice_flow && (
            <div className="mt-3 flex flex-wrap gap-2">
              {v.practice_flow.map((step, i) => (
                <span key={i} className="inline-flex items-center gap-1 rounded-full bg-[#F0F4F8] border border-[#DDE6F0] text-[#3B597B] text-[11px] font-semibold uppercase tracking-[0.12em] px-3 py-1">
                  {step}
                </span>
              ))}
            </div>
          )}
          <p className="mt-3 text-[13px] uppercase tracking-[0.12em] font-semibold text-slate-500">Practice</p>
          <p className="mt-1 text-slate-800">{v.practice}</p>
        </div>
      ))}
      {section.closing_lines && (
        <div className="rounded-lg bg-[#FBF8F1] border-l-4 border-[#C9A55C] px-4 py-3">
          <ul className="space-y-0.5">
            {section.closing_lines.map((l, i) => (
              <li key={i} className="font-heading text-slate-900">{l}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function OfficialFileBody({ section }) {
  const downloadGuide = async () => {
    try {
      const fd2g = fd2gApi();
      const r = await fd2g.get(`resource-file/${section.download_slug}`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = section.download_filename || `${section.download_slug}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Download failed.";
      toast.error(typeof msg === "string" ? msg : "Download failed.");
    }
  };
  return (
    <div className="space-y-4">
      <p className="text-slate-700">{section.description}</p>
      <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-3">
        <p className="text-[11px] uppercase tracking-[0.16em] font-semibold text-slate-500">{section.framework?.heading}</p>
        <ul className="mt-2 space-y-1 text-sm text-slate-800">
          {(section.framework?.steps || []).map((s, i) => (
            <li key={i} className="flex justify-between gap-4">
              <span>{s.label}</span><span className="text-slate-500">{s.duration}</span>
            </li>
          ))}
        </ul>
      </div>
      <div>
        <p className="text-[11px] uppercase tracking-[0.16em] font-semibold text-slate-500">{section.facilitator_commitments?.heading}</p>
        <ul className="mt-2 space-y-1.5 text-sm text-slate-800 list-disc pl-5">
          {(section.facilitator_commitments?.items || []).map((c, i) => <li key={i}>{c}</li>)}
        </ul>
      </div>
      {section.file_state === "available" && section.download_slug && (
        <div className="rounded-lg border border-[#C9A55C] bg-[#FBF8F1] px-4 py-3 flex flex-wrap items-center justify-between gap-3" data-testid="fd2gfac-rc-guide-available">
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#8A7238]">{section.file_label}</p>
            <p className="mt-1 text-sm text-slate-800">Official file available for authorized facilitators.</p>
          </div>
          <button onClick={downloadGuide} data-testid="fd2gfac-rc-guide-download"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#4A6FA5] text-white text-sm font-semibold hover:bg-[#3B597B]">
            <FileText className="w-4 h-4" /> DOWNLOAD PDF
          </button>
        </div>
      )}
      {section.file_state === "pending_admin_upload" && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3" data-testid="fd2gfac-rc-guide-pending">
          <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-amber-800">{section.file_label}</p>
          <p className="mt-1 text-sm text-amber-900">{section.note}</p>
        </div>
      )}
      {section.file_state === "available" && section.note && (
        <p className="text-[12px] italic text-slate-500">{section.note}</p>
      )}
    </div>
  );
}

function FormatCardsBody({ section }) {
  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-3">
        {(section.cards || []).map((f, i) => (
          <div key={i} className="rounded-lg border border-slate-200 bg-white p-4">
            <p className="text-[11px] uppercase tracking-[0.14em] font-semibold text-[#4A6FA5]">{f.name}</p>
            <p className="mt-2 text-[12px] uppercase tracking-[0.12em] font-semibold text-slate-500">Best for</p>
            <p className="mt-1 text-sm text-slate-800">{f.best_for}</p>
          </div>
        ))}
      </div>
      {section.principle && (
        <div className="rounded-lg bg-[#FBF8F1] border-l-4 border-[#C9A55C] px-4 py-3">
          <p className="text-[10px] uppercase tracking-[0.16em] font-semibold text-[#8A7238]">Principle</p>
          <p className="mt-1 text-slate-900 italic">"{section.principle.label}"</p>
          <ul className="mt-2 space-y-0.5 text-slate-700">
            {(section.principle.lines || []).map((l, i) => <li key={i}>{l}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}

function OpeningBody({ section }) {
  return (
    <div className="space-y-4">
      <div className="space-y-3">
        {(section.framework || []).map((step, i) => (
          <div key={i} className="rounded-lg border border-slate-200 bg-white p-4">
            <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#4A6FA5]">{step.label}</p>
            {step.text && <p className="mt-1 text-slate-800">{step.text}</p>}
            {step.lines && (
              <ul className="mt-1 space-y-0.5 text-slate-800">
                {step.lines.map((l, j) => <li key={j}>{l}</li>)}
              </ul>
            )}
          </div>
        ))}
      </div>
      {section.reminder && (
        <div className="rounded-lg bg-[#F0F4F8] border-l-4 border-[#4A6FA5] px-4 py-3">
          <p className="text-[10px] uppercase tracking-[0.16em] font-semibold text-[#3B597B]">{section.reminder.label}</p>
          <ul className="mt-1 space-y-0.5 text-slate-900">
            {(section.reminder.lines || []).map((l, i) => <li key={i} className="italic">"{l}"</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}

function QuestionBankBody({ section }) {
  return (
    <div className="space-y-4">
      {(section.banks || []).map((bank) => (
        <div key={bank.voice} className="rounded-lg border border-slate-200 bg-white p-4">
          <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#4A6FA5]">{bank.voice}</p>
          <ul className="mt-2 space-y-1.5 text-sm text-slate-800 list-disc pl-5">
            {bank.questions.map((q, i) => <li key={i}>{q}</li>)}
          </ul>
        </div>
      ))}
      {section.reminder_lines && (
        <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-3">
          <ul className="space-y-0.5 text-slate-700 italic">
            {section.reminder_lines.map((l, i) => <li key={i}>{l}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Voice Map worksheet — session-only, in-memory. NEVER persisted.
// No localStorage · no cookies · no analytics · no URLs · no server writes.
// Refreshing or leaving the Resource Center clears the worksheet by design.
// ---------------------------------------------------------------------------
function VoiceMapBody({ section }) {
  const [rows, setRows] = useState(() =>
    Array.from({ length: section.blank_rows || 5 }, () => ({ voice: "", know: "", need: "" }))
  );

  const setRow = (idx, key, value) =>
    setRows((r) => r.map((row, i) => (i === idx ? { ...row, [key]: value } : row)));

  const clearAll = () => {
    setRows(Array.from({ length: section.blank_rows || 5 }, () => ({ voice: "", know: "", need: "" })));
    toast.success("Voice Map cleared.");
  };
  const printMap = () => { window.print(); };

  return (
    <div className="space-y-4">
      <div className="rounded-lg bg-rose-50 border-l-4 border-rose-300 px-4 py-3">
        <p className="text-[10px] uppercase tracking-[0.16em] font-semibold text-rose-800">{section.rule?.label}</p>
        <p className="mt-1 text-rose-900 italic">"{section.rule?.text}"</p>
      </div>
      <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-3" data-testid="fd2gfac-rc-voice-map-privacy-note">
        <p className="text-[11px] uppercase tracking-[0.16em] font-semibold text-slate-700">Privacy</p>
        <p className="mt-1 text-[12px] text-slate-800">
          Use this worksheet as a facilitation aid. Do not enter unnecessary personally identifiable, medical, or
          confidential information.
        </p>
        <p className="mt-2 text-[12px] italic text-slate-600" data-testid="fd2gfac-rc-voice-map-persistence-note">
          Voice Map entries stay in this browser session only. Nothing is sent to the server, saved to your device,
          or logged anywhere. Refreshing or leaving the Resource Center will clear the worksheet.
        </p>
      </div>
      <div className="overflow-x-auto rounded-lg border border-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              {(section.columns || []).map((col, i) => (
                <th key={i} className="text-left text-[10px] uppercase tracking-[0.14em] font-semibold text-slate-600 px-3 py-2">{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr key={idx} className="border-t border-slate-100 align-top">
                <td className="px-3 py-2 w-[18%]">
                  <input
                    className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm"
                    placeholder="e.g. Patient"
                    value={row.voice}
                    onChange={(e) => setRow(idx, "voice", e.target.value)}
                    data-testid={`fd2gfac-rc-vm-voice-${idx}`}
                  />
                </td>
                <td className="px-3 py-2 w-[41%]">
                  <textarea
                    className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm min-h-[44px]"
                    placeholder="Only what is directly observable."
                    value={row.know}
                    onChange={(e) => setRow(idx, "know", e.target.value)}
                    data-testid={`fd2gfac-rc-vm-know-${idx}`}
                  />
                </td>
                <td className="px-3 py-2 w-[41%]">
                  <textarea
                    className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm min-h-[44px]"
                    placeholder="What is still unknown or unverified."
                    value={row.need}
                    onChange={(e) => setRow(idx, "need", e.target.value)}
                    data-testid={`fd2gfac-rc-vm-need-${idx}`}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap gap-2">
        <button onClick={clearAll} data-testid="fd2gfac-rc-vm-clear"
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-slate-300 text-sm text-slate-700 hover:border-[#4A6FA5]">
          <RefreshCw className="w-4 h-4" /> Clear worksheet
        </button>
        {section.print_supported && (
          <button onClick={printMap} data-testid="fd2gfac-rc-vm-print"
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-slate-300 text-sm text-slate-700 hover:border-[#4A6FA5]">
            <Printer className="w-4 h-4" /> Print / Save PDF
          </button>
        )}
      </div>
      {section.facilitator_check && (
        <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-3">
          <p className="text-[11px] uppercase tracking-[0.16em] font-semibold text-slate-600">{section.facilitator_check.heading}</p>
          <ul className="mt-2 space-y-1 text-sm text-slate-800 list-disc pl-5">
            {section.facilitator_check.questions.map((q, i) => <li key={i}>{q}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}

function ScenariosBody({ section }) {
  return (
    <div className="space-y-3">
      {(section.scenarios || []).map((s, i) => (
        <div key={i} className="rounded-lg border border-slate-200 bg-white p-4">
          <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#4A6FA5]">{s.situation}</p>
          {s.guidance && (
            <ul className="mt-2 space-y-1 text-sm text-slate-800 list-disc pl-5">
              {s.guidance.map((g, j) => <li key={j}>{g}</li>)}
            </ul>
          )}
          {s.possible_response && (
            <div className="mt-3 rounded-md bg-[#F0F4F8] border-l-4 border-[#4A6FA5] px-3 py-2">
              <p className="text-[10px] uppercase tracking-[0.14em] font-semibold text-[#3B597B]">Possible response</p>
              <p className="mt-1 text-slate-800 italic">{s.possible_response}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function BoundariesBody({ section }) {
  return (
    <div className="space-y-4">
      <div className="rounded-lg bg-slate-50 border-l-4 border-slate-300 px-4 py-3">
        <p className="text-slate-900 font-semibold">{section.principle}</p>
      </div>
      {(section.phrases || []).map((p, i) => (
        <div key={i} className="rounded-lg border border-slate-200 bg-white p-4">
          <p className="text-[11px] uppercase tracking-[0.16em] font-semibold text-slate-500">{p.label}</p>
          <p className="mt-1 text-slate-800 italic">"{p.text}"</p>
        </div>
      ))}
      {section.referral_categories && (
        <div>
          <p className="text-[11px] uppercase tracking-[0.16em] font-semibold text-slate-500">{section.referral_categories.heading}</p>
          <ul className="mt-2 space-y-1 text-sm text-slate-800 list-disc pl-5">
            {section.referral_categories.items.map((it, i) => <li key={i}>{it}</li>)}
          </ul>
        </div>
      )}
      {section.warning && (
        <div className="rounded-lg bg-amber-50 border-l-4 border-amber-300 px-4 py-3">
          <p className="text-amber-900 font-semibold">{section.warning}</p>
        </div>
      )}
    </div>
  );
}

function MaterialsBody({ section }) {
  const downloadMaterial = async (m) => {
    try {
      const fd2g = fd2gApi();
      const r = await fd2g.get(`resource-file/${m.download_slug}`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = m.download_filename || `${m.download_slug}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Download failed.";
      toast.error(typeof msg === "string" ? msg : "Download failed.");
    }
  };
  return (
    <div className="space-y-4">
      {(section.materials || []).map((m, i) => (
        <div key={i} className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-slate-900">{m.name}</p>
              <p className="mt-1 text-sm text-slate-700">{m.description}</p>
              {m.file_state === "pending_admin_upload" && (
                <div className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-amber-50 border border-amber-200 px-2.5 py-1 text-[10px] uppercase tracking-[0.14em] font-semibold text-amber-800">
                  {m.file_label}
                </div>
              )}
              {m.file_state === "available" && (
                <div className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-[#FBF8F1] border border-[#C9A55C] px-2.5 py-1 text-[10px] uppercase tracking-[0.14em] font-semibold text-[#8A7238]">
                  {m.file_label}
                </div>
              )}
            </div>
            {m.file_state === "available" && m.download_slug && (
              <button
                onClick={() => downloadMaterial(m)}
                data-testid={`fd2gfac-rc-material-download-${m.download_slug}`}
                className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg bg-[#4A6FA5] text-white text-sm font-semibold hover:bg-[#3B597B] shrink-0"
              >
                <FileText className="w-4 h-4" /> DOWNLOAD PDF
              </button>
            )}
          </div>
        </div>
      ))}
      {section.note && <p className="text-[12px] italic text-slate-500">{section.note}</p>}
    </div>
  );
}

function BrandingBody({ section }) {
  return (
    <div className="space-y-4">
      <div className="rounded-lg bg-[#FBF8F1] border-l-4 border-[#C9A55C] px-4 py-3">
        <p className="text-[10px] uppercase tracking-[0.16em] font-semibold text-[#8A7238]">Approved designation while ACTIVE</p>
        <p className="mt-1 font-heading text-slate-900 text-lg">{section.approved_designation}</p>
        <p className="mt-1 text-[12px] text-slate-600">Credential Code: <strong>{section.credential_code}</strong></p>
      </div>
      <div>
        <p className="text-[11px] uppercase tracking-[0.16em] font-semibold text-emerald-700">May say</p>
        <ul className="mt-2 space-y-1 text-sm text-slate-800 list-disc pl-5">
          {section.may_say.map((s, i) => <li key={i} className="italic">"{s.replace(/^"|"$/g, "").replace(/\u201c|\u201d/g, "")}"</li>)}
        </ul>
      </div>
      <div>
        <p className="text-[11px] uppercase tracking-[0.16em] font-semibold text-rose-700">May not claim</p>
        <ul className="mt-2 space-y-1 text-sm text-slate-800 list-disc pl-5">
          {section.may_not_claim.map((s, i) => <li key={i}>{s}</li>)}
        </ul>
      </div>
      <div className="rounded-lg bg-[#F0F4F8] border-l-4 border-[#4A6FA5] px-4 py-3">
        <p className="text-slate-800 italic">"{section.display_quote}"</p>
      </div>
    </div>
  );
}

function UpdatesBody({ section }) {
  const updates = section.updates || [];
  return (
    <div className="space-y-4">
      {updates.length === 0 ? (
        <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-4 text-center" data-testid="fd2gfac-rc-updates-empty">
          <CheckCircle2 className="w-5 h-5 text-emerald-600 mx-auto" />
          <p className="mt-2 text-sm text-slate-800">{section.empty_state}</p>
        </div>
      ) : (
        <ul className="space-y-2">
          {updates.map((u, i) => (
            <li key={i} className="rounded-lg border border-slate-200 bg-white p-3 text-sm text-slate-800">
              <p className="font-semibold">{u.title}</p>
              {u.body && <p className="mt-1 text-slate-700">{u.body}</p>}
              {u.date && <p className="mt-1 text-[11px] text-slate-500">{u.date}</p>}
            </li>
          ))}
        </ul>
      )}
      {section.categories && (
        <div>
          <p className="text-[11px] uppercase tracking-[0.16em] font-semibold text-slate-500">Update categories</p>
          <ul className="mt-2 space-y-1 text-sm text-slate-700 list-disc pl-5">
            {section.categories.map((c, i) => <li key={i}>{c}</li>)}
          </ul>
        </div>
      )}
      {section.note && <p className="text-[12px] italic text-slate-500">{section.note}</p>}
    </div>
  );
}
